/**
 * Utility functions for translating animal attributes (size, adjectives, age, etc.)
 */
import i18n from "i18next";

/**
 * Translates size values to Portuguese display strings with proper casing
 * @param size - Size string (any casing)
 * @returns Translated and formatted size string
 */
export const translateSize = (size: string): string => {
  if (!size) return "";
  // Normalize input and map common keys to their desired display values
  const normalized = size.trim().toLowerCase();

  const map: Record<string, "small" | "medium" | "large" | "extraLarge"> = {
    pequeno: "small",
    medio: "medium",
    médio: "medium",
    grande: "large",
    xl: "extraLarge",
    "extra grande": "extraLarge",
    "extra\tgrande": "extraLarge",
  };

  if (map[normalized]) return i18n.t(`utils:size.${map[normalized]}`);

  // Fallback: capitalize first letter using locale-aware methods (pt-BR)
  const first = normalized.charAt(0).toLocaleUpperCase("pt-BR");
  const rest = normalized.slice(1).toLocaleLowerCase("pt-BR");
  return `${first}${rest}`;
};

// Backend sends age stages as Portuguese enum values; map them to labels at render.
const AGE_STAGE_KEYS: Record<string, "puppy" | "young" | "adult" | "senior"> = {
  filhote: "puppy",
  jovem: "young",
  adulto: "adult",
  "sénior": "senior",
  senior: "senior",
};

export const translateAgeStage = (stage?: string | null): string => {
  if (!stage) return "";
  const key = AGE_STAGE_KEYS[stage.trim().toLowerCase()];
  return key ? i18n.t(`utils:ageStage.${key}`) : stage;
};

export const translateGender = (gender?: string | null): string => {
  if (!gender) return "";
  const normalized = gender.trim().toUpperCase();
  if (normalized === "MACHO") return i18n.t("common:animal.male");
  if (normalized === "FÊMEA" || normalized === "FEMEA") return i18n.t("common:animal.female");
  return gender;
};

/**
 * Translates adjectives to Portuguese display strings with proper casing
 * @param adjective - Adjective string (any casing)
 * @returns Translated and formatted adjective string
 */
export const translateAdjective = (adjective: string): string => {
  if (!adjective) return "";
  const normalized = adjective.trim().toLowerCase();
  // Use locale-aware capitalization for Portuguese
  const first = normalized.charAt(0).toLocaleUpperCase("pt-BR");
  const rest = normalized.slice(1).toLocaleLowerCase("pt-BR");
  return `${first}${rest}`;
};

/**
 * Translates an array or comma-separated string of adjectives
 * @param adjectives - Array or string of adjectives
 * @returns Array of translated adjectives
 */
export const translateAdjectives = (
  adjectives: string | string[],
): string[] => {
  if (!adjectives) return [];
  if (Array.isArray(adjectives)) {
    return adjectives.map(translateAdjective);
  }
  return adjectives.split(",").map((a) => translateAdjective(a.trim()));
};
/**
 * Translates age in months to Portuguese life stage categories for dogs/cats
 * @param ageInMonths - Age in months
 * @returns Portuguese life stage string
 */
export const getAgeStage = (ageInMonths: number): string => {
  // Age stages (in months):
  // Filhote: <= 9 months
  // Jovem: <= 36 months (3 years)
  // Adulto: <= 84 months (7 years)
  // Sénior: <= 168 months (14 years)
  // Highest ages are grouped into 'Sénior'
  if (ageInMonths <= 9) {
    return i18n.t("utils:ageStage.puppy");
  } else if (ageInMonths <= 36) {
    return i18n.t("utils:ageStage.young");
  } else if (ageInMonths <= 84) {
    return i18n.t("utils:ageStage.adult");
  } else if (ageInMonths <= 168) {
    return i18n.t("utils:ageStage.senior");
  } else {
    return i18n.t("utils:ageStage.senior");
  }
};

/**
 * Converts age in months to years and months display
 * @param ageInMonths - Age in months
 * @returns Formatted age string (e.g., "2 anos e 3 meses", "8 meses")
 */
export const formatAge = (ageInMonths: number): string => {
  const years = Math.floor(ageInMonths / 12);
  const months = ageInMonths % 12;

  if (years === 0) {
    return i18n.t("utils:age.months", { count: months });
  } else if (months === 0) {
    return i18n.t("utils:age.years", { count: years });
  } else {
    return i18n.t("utils:age.yearsAndMonths", {
      years: i18n.t("utils:age.years", { count: years }),
      months: i18n.t("utils:age.months", { count: months }),
    });
  }
};

/**
 * Gets both age stage and formatted age
 * @param ageInMonths - Age in months
 * @returns Object with stage and formatted age
 */
export const getAgeInfo = (ageInMonths: number) => ({
  stage: getAgeStage(ageInMonths),
  formatted: formatAge(ageInMonths),
});
